import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useInsightsFiltersAndOrder } from '@/components/loop-dashboard/hooks/useInsightsFiltersAndOrder';
import type { Tab } from '@/components/loop-dashboard/types/tabs';
import type { FiltersAppliedValueType } from '@/lib/analytics/Avo';
import { FiltersApplied, insightsDashboardViewed, OriginAction } from '@/lib/analytics/Avo';
import type { ParsedInsightsFilters } from '@/lib/api/types/insights/filters';
import { useItemAncestorsQuery } from '@/lib/queries/common/useGetItemAncestorsQuery';
import type { ParsedDateFilter } from '@/lib/utils/compact-filters/transformCompactFilters';
import { getOriginView } from './getOriginView';
import { consumeNextNavigationFromSearch } from './trackItemSearched';

interface TrackingParams {
  currentTab: Tab;
  scopeItemId?: number;
}

const SORT_FIELD_TO_ANALYTICS_MAP: Record<string, string> = {
  plannedStartDateTime: 'Planned Start',
  plannedFinishDateTime: 'Planned Finish',
  priority: 'Priority',
};

type SearchParams = ReturnType<typeof useSearch>;

const hasFilterValues = (filter: number[] | ParsedDateFilter | undefined): boolean => {
  if (!filter) return false;
  if (Array.isArray(filter)) return filter.length > 0;
  if (filter.operator === 'null' || filter.operator === 'notnull') return true;
  return (filter?.value?.length ?? 0) > 0;
};

const normalizeFilters = (filters: ParsedInsightsFilters): ParsedInsightsFilters =>
  Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      key,
      hasFilterValues(value) ? value : undefined,
    ]),
  ) as ParsedInsightsFilters;

const filtersChanged = (prev: ParsedInsightsFilters, current: ParsedInsightsFilters): boolean =>
  JSON.stringify(normalizeFilters(prev)) !== JSON.stringify(normalizeFilters(current));

const getOriginAction = (
  isInitialMount: boolean,
  prevSearch: SearchParams,
  currentSearch: SearchParams,
  prevFilters: ParsedInsightsFilters,
  currentFilters: ParsedInsightsFilters,
  currentLevel: number,
  prevLevel: number,
  fromSearch: boolean,
) => {
  if (isInitialMount) return OriginAction.NAVIGATION;
  if (prevSearch.scopeItemId !== currentSearch.scopeItemId) {
    const isDrillDown = currentLevel > prevLevel;
    if (fromSearch) {
      return isDrillDown
        ? OriginAction.DRILL_DOWN_FROM_SEARCH
        : OriginAction.BREAD_CRUMB_FROM_SEARCH;
    }
    return isDrillDown ? OriginAction.DRILL_DOWN : OriginAction.BREAD_CRUMB;
  }
  if (
    prevSearch.orderBy !== currentSearch.orderBy ||
    prevSearch.orderDir !== currentSearch.orderDir
  ) {
    return OriginAction.UPDATED_SORTING;
  }

  if (filtersChanged(prevFilters, currentFilters)) {
    return OriginAction.UPDATED_FILTER;
  }

  return OriginAction.NAVIGATION;
};

const FILTER_TO_ANALYTICS_MAP: Array<[keyof ParsedInsightsFilters, FiltersAppliedValueType]> = [
  ['teamId', FiltersApplied.TEAM],
  ['itemTypeId', FiltersApplied.ITEM_TYPE],
  ['statusId', FiltersApplied.STATUS],
  ['integrationId', FiltersApplied.INTEGRATION],
  ['plannedStartDateTime', FiltersApplied.PLANNED_START_DATE],
  ['plannedFinishDateTime', FiltersApplied.PLANNED_FINISH_DATE],
];

const getAppliedFilters = (parsed: ParsedInsightsFilters): FiltersAppliedValueType[] =>
  FILTER_TO_ANALYTICS_MAP.flatMap(([key, value]) => (hasFilterValues(parsed[key]) ? [value] : []));

export const useTrackInsightsDashboardViewed = ({ currentTab, scopeItemId }: TrackingParams) => {
  const search = useSearch({ strict: false });
  const { filters: currentFilters } = useInsightsFiltersAndOrder();
  const prevSearchRef = useRef(search);
  const prevFiltersRef = useRef(currentFilters);
  const prevLevelRef = useRef(0);
  const isInitialMount = useRef(true);

  const { data: ancestors = [], isLoading: isLoadingAncestors } = useItemAncestorsQuery({
    itemId: scopeItemId || 0,
  });

  useEffect(() => {
    if (scopeItemId && isLoadingAncestors) {
      return;
    }

    const currentLevel = scopeItemId ? ancestors.length + 1 : 0;
    const prevFilters = prevFiltersRef.current;
    const filters = currentFilters;

    const originAction = getOriginAction(
      isInitialMount.current,
      prevSearchRef.current,
      search,
      prevFilters,
      filters,
      currentLevel,
      prevLevelRef.current,
      false,
    );

    if (isInitialMount.current || originAction !== OriginAction.NAVIGATION) {
      const fromSearch = consumeNextNavigationFromSearch();
      const finalOriginAction = getOriginAction(
        isInitialMount.current,
        prevSearchRef.current,
        search,
        prevFilters,
        filters,
        currentLevel,
        prevLevelRef.current,
        fromSearch,
      );

      insightsDashboardViewed({
        originView: getOriginView(currentTab),
        filtersApplied: getAppliedFilters(filters),
        sortedBy:
          (search.orderBy && SORT_FIELD_TO_ANALYTICS_MAP[search.orderBy]) || search.orderBy || '',
        originAction: finalOriginAction,
        numberOfLevels: currentLevel,
      });

      isInitialMount.current = false;
    }

    prevSearchRef.current = search;
    prevFiltersRef.current = filters;
    prevLevelRef.current = currentLevel;
  }, [search, currentTab, scopeItemId, ancestors.length, currentFilters, isLoadingAncestors]);
};
